import type { FC, ReactNode } from "react";

import {
  Button,
  Dialog as DialogCore,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslation } from "react-i18next";

interface DialogProps {
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  title: string;
  variant: "error" | "warning" | "info";
  confirmButtonText?: string;
  cancelButtonText?: string;
  isConfirmButtonDisabled?: boolean;
  confirmButtonId?: string;
  cancelButtonId?: string;
}

const icons = {
  error: <ErrorIcon color="error" fontSize="large" />,
  warning: <WarningIcon color="warning" fontSize="large" />,
  info: <InfoIcon color="info" fontSize="large" />,
};

export const Dialog: FC<DialogProps> = ({
  children,
  onCancel,
  onConfirm,
  open,
  title,
  variant,
  confirmButtonText,
  cancelButtonText,
  isConfirmButtonDisabled = false,
  confirmButtonId,
  cancelButtonId,
}) => {
  const { t } = useTranslation();

  return (
    <DialogCore
      onClose={onCancel}
      open={open}
      PaperProps={{
        sx: {
          width: "100%",
        },
      }}
    >
      <DialogTitle
        sx={{
          alignItems: "center",
          display: "flex",
        }}
      >
        {icons[variant]}
        <Typography color="inherit" sx={{ ml: 2 }} variant="inherit">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel} variant="text" id={cancelButtonId}>
          {cancelButtonText ?? t("common.button.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={onConfirm}
          variant="contained"
          id={confirmButtonId}
          disabled={isConfirmButtonDisabled}
        >
          {confirmButtonText ?? t("common.button.save")}
        </Button>
      </DialogActions>
    </DialogCore>
  );
};
